import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    MonitorPlay, Stethoscope, Building2, Users, Copy, ExternalLink,
    ArrowRight, ClipboardList, FileText, Workflow, ShieldCheck,
} from "lucide-react";

/* One place to pull up the GUIDE demo live:
 *   1. Care navigator view  (/org-intel?org=demo)  — same admin session
 *   2. Org admin view       (/org-admin?org=demo)  — same admin session
 *   3. Family view          — demo.family credentials for an incognito window
 * Plus the GUIDE-systems integration talking points for partner calls.
 */

const FAMILY_EMAIL = "demo.family@memorylane.app";
const FAMILY_PASS = "DemoFamily2026!";
const NAV_EMAIL = "demo-navigator@memorylane.app";
const NAV_PASS = "Navigator2026!";

export default function GuideDemoView() {
    const [demoOrg, setDemoOrg] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/org/admin/list")
            .then(r => {
                const list = Array.isArray(r.data) ? r.data : r.data.orgs || [];
                setDemoOrg(list.find(o => (o.name || "").includes("(Demo)")) || null);
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const copy = async (text, label) => {
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied.`); }
        catch { toast("Copy failed — select it manually."); }
    };

    return (
        <div className="space-y-6" data-testid="guide-demo-view">
            <div className="ml-card p-6 bg-white">
                <div className="flex items-center gap-2 mb-1">
                    <MonitorPlay size={18} className="text-teal-dark" strokeWidth={1.8} />
                    <h2 className="text-lg font-heading font-semibold text-[#1F2624]">GUIDE demo — pull it up in one click</h2>
                </div>
                <p className="text-sm text-[#4C5551] max-w-2xl">
                    A seeded GUIDE program ({demoOrg?.name || "Sample GUIDE Program"}) with a real-looking
                    family (Maria Alvarez caring for her mom Sofia). Open each side below — you stay signed in as admin.
                </p>
                {loaded && !demoOrg && (
                    <p className="mt-3 text-sm text-coral-dark" data-testid="guide-demo-missing">
                        The demo org isn&apos;t in the database (it may have been deleted). Restart the backend to re-seed it,
                        or contact support if it stays missing.
                    </p>
                )}
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <a
                        href={demoOrg ? `/org-intel?org=${demoOrg.id}` : "/org-intel"}
                        target="_blank" rel="noreferrer"
                        data-testid="guide-demo-open-navigator"
                        className="group rounded-2xl border border-teal-light bg-teal-soft/30 p-4 hover:border-teal transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <Stethoscope size={16} className="text-teal-dark" />
                            <p className="font-medium text-[#1F2624]">What a care navigator sees</p>
                            <ExternalLink size={13} className="text-[#6E7A74] ml-auto group-hover:text-teal-dark" />
                        </div>
                        <p className="text-xs text-[#4C5551] mt-1.5">
                            Caseload sorted by risk, alerts since last contact, visit prep, clinical summaries, care plans —
                            filtered by exactly what each family chose to share.
                        </p>
                    </a>
                    <a
                        href={demoOrg ? `/org-admin?org=${demoOrg.id}` : "/org-admin"}
                        target="_blank" rel="noreferrer"
                        data-testid="guide-demo-open-orgadmin"
                        className="group rounded-2xl border border-cream-dark bg-white p-4 hover:border-teal transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <Building2 size={16} className="text-ml_blue" />
                            <p className="font-medium text-[#1F2624]">What their program admin sees</p>
                            <ExternalLink size={13} className="text-[#6E7A74] ml-auto group-hover:text-teal-dark" />
                        </div>
                        <p className="text-xs text-[#4C5551] mt-1.5">
                            Enrollment funnel, engaged families, invite codes, co-branding — the aggregate program view,
                            never individual data beyond what families granted.
                        </p>
                    </a>
                </div>
            </div>

            <div className="ml-card p-6 bg-white">
                <div className="flex items-center gap-2 mb-1">
                    <Users size={16} className="text-teal-dark" strokeWidth={1.8} />
                    <h3 className="font-heading font-semibold text-[#1F2624]">Family side (open in an incognito window)</h3>
                </div>
                <p className="text-sm text-[#4C5551]">
                    Show the caregiver-owned side — journey, behaviors, sharing toggles, access history — as Maria Alvarez.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                    <button type="button" onClick={() => copy(`${FAMILY_EMAIL} / ${FAMILY_PASS}`, "Family login")}
                        data-testid="guide-demo-copy-family"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                        <Copy size={13} /> {FAMILY_EMAIL} <span className="text-[#6E7A74]">/ {FAMILY_PASS}</span>
                    </button>
                    <button type="button" onClick={() => copy(`${NAV_EMAIL} / ${NAV_PASS}`, "Navigator login")}
                        data-testid="guide-demo-copy-navigator"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                        <Copy size={13} /> {NAV_EMAIL} <span className="text-[#6E7A74]">/ {NAV_PASS}</span>
                    </button>
                </div>
            </div>

            <div className="ml-card p-6 bg-white" data-testid="guide-demo-integration">
                <div className="flex items-center gap-2 mb-2">
                    <Workflow size={16} className="text-teal-dark" strokeWidth={1.8} />
                    <h3 className="font-heading font-semibold text-[#1F2624]">How we integrate with GUIDE systems — the pitch</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-[#4C5551]">
                    <div className="space-y-3">
                        <div className="flex gap-2.5">
                            <ClipboardList size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">Today — no IT project needed.</strong> Navigators work in the
                            web workspace; families connect with a one-time invite code. Zero EHR integration required to start a pilot.</p>
                        </div>
                        <div className="flex gap-2.5">
                            <FileText size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">Documentation that fits their workflow.</strong> One-click
                            clinical summaries, visit prep, and care plans export as PDF/text — navigators paste them straight into
                            their GUIDE documentation and CMS reporting.</p>
                        </div>
                        <div className="flex gap-2.5">
                            <ShieldCheck size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">GUIDE-aligned by design.</strong> Caregiver strain (Zarit-informed),
                            behavior logs, respite-relevant burden signals, and 24/7 caregiver support map to GUIDE care-model
                            requirements — with a consented, auditable trail.</p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex gap-2.5">
                            <ArrowRight size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">Next — structured export.</strong> The longitudinal record is
                            already structured (stages, behaviors, meds, care team), so CSV/API export to their population-health
                            tools is a configuration, not a rebuild.</p>
                        </div>
                        <div className="flex gap-2.5">
                            <ArrowRight size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">As programs mature — SMART on FHIR.</strong> Data is structured
                            to map onto FHIR resources (Patient, Observation, CarePlan) when a health system wants EHR-embedded
                            views. Roadmap, not a promise of current certification.</p>
                        </div>
                        <div className="flex gap-2.5">
                            <ArrowRight size={15} className="text-teal-dark shrink-0 mt-0.5" />
                            <p><strong className="text-[#1F2624]">The differentiator.</strong> The family owns the record and it
                            travels with them across providers — GUIDE programs get continuity other vendors can&apos;t offer,
                            because our record doesn&apos;t reset when the provider changes.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
